import React from "react";
import { Modal as AntdModal, Button, Typography } from "antd";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { GROUP_COLORS, getParentGroup, getChildLabel } from "../constants/callConstants";

const { Text } = Typography;

interface CallHistoryModalProps {
    open: boolean;
    logs: any[];
    onClose: () => void;
}

const CallHistoryModal: React.FC<CallHistoryModalProps> = ({ open, logs, onClose }) => {

    return (
        <AntdModal
            open={open}
            onCancel={onClose}
            centered
            width={560}
            maskClosable={false}
            keyboard={false}
            zIndex={9999}
            getContainer={() => document.body}
            styles={{
                content: {
                    padding: 0, display: "flex", flexDirection: "column",
                    maxHeight: "90vh", overflow: "hidden", borderRadius: 12,
                },
                header: {
                    flexShrink: 0, padding: "16px 24px 12px",
                    borderBottom: "1px solid #f0f0f0", marginBottom: 0,
                },
                body: {
                    flex: 1, overflowY: "auto", overflowX: "hidden",
                    padding: "20px 24px 8px",
                    scrollbarWidth: "thin" as any, scrollbarColor: "#d1d5db transparent",
                },
                footer: {
                    flexShrink: 0, padding: "12px 24px 16px",
                    borderTop: "1px solid #f0f0f0", marginTop: 0,
                },
                mask: {
                    position: "fixed", inset: 0, width: "100vw", height: "100vh",
                    backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
                    background: "rgba(0, 0, 0, 0.45)",
                },
            }}
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>Call History</span>
                    <span style={{
                        fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 10,
                        background: "#dbeafe", color: "#1e40af", border: "1px solid #bfdbfe",
                    }}>
                        {logs.length} {logs.length === 1 ? "entry" : "entries"}
                    </span>
                </div>
            }
            footer={
                <div style={{ textAlign: "right" }}>
                    <Button onClick={onClose}>Close</Button>
                </div>
            }
        >
            {logs?.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <span style={{ fontSize: 28 }}>📭</span>
                    <p style={{ marginTop: 8, fontSize: 13, color: "#9ca3af" }}>No call history available</p>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {logs?.map((log, i) => {
                        const parent = getParentGroup(log.type);
                        const colors = GROUP_COLORS[parent] ?? GROUP_COLORS["Other"];

                        return (
                            <div
                                key={i}
                                style={{
                                    border: "1px solid #e5e7eb",
                                    borderLeft: `3px solid ${colors.accent}`,
                                    borderRadius: 10,
                                    padding: "12px 14px",
                                    background: "#fff",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 10,
                                }}
                            >
                                {/* Left: colored icon dot */}
                                <span style={{
                                    minWidth: 32, height: 32, borderRadius: "50%",
                                    background: colors.bg,
                                    border: `1px solid ${colors.accent}30`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    flexShrink: 0,
                                }}>
                                    <i className="ti ti-phone-call" style={{ fontSize: 14, color: colors.accent }} />
                                </span>

                                {/* Right: content */}
                                <div style={{ flex: 1, minWidth: 0 }}>

                                    {/* Row 1: label + date */}
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <Text strong style={{ fontSize: 13, color: "#111827" }}>
                                                {getChildLabel(log.type)}
                                            </Text>
                                            <span style={{
                                                fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 8,
                                                background: colors.tagBg, color: colors.tagColor,
                                                border: `1px solid ${colors.tagBg}`,
                                                textTransform: "uppercase", letterSpacing: 0.5,
                                            }}>
                                                {parent}
                                            </span>
                                        </div>
                                        <span style={{
                                            fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", flexShrink: 0,
                                            background: "#f3f4f6", border: "1px solid #e5e7eb",
                                            borderRadius: 8, padding: "1px 7px",
                                        }}>
                                            {humanReadableDate(log.date, true)}
                                        </span>
                                    </div>

                                    {/* Row 2: note */}
                                    {log.note && (
                                        <div style={{
                                            marginTop: 4, padding: "5px 9px",
                                            background: "#f9fafb", borderRadius: 6,
                                            border: "1px solid #f3f4f6",
                                        }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Note — </Text>
                                            <Text style={{ fontSize: 12 }}>{log.note}</Text>
                                        </div>
                                    )}

                                    {/* Row 3: reminder */}
                                    {log.reminder_date && (
                                        <div style={{
                                            marginTop: 4, display: "flex", alignItems: "center", gap: 4,
                                        }}>
                                            <span style={{ fontSize: 11 }}>🔔</span>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Reminder — </Text>
                                            <Text style={{ fontSize: 11, color: "#f59e0b", fontWeight: 500 }}>
                                                {humanReadableDate(log.reminder_date, true)}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </AntdModal>
    );
};

export default CallHistoryModal;
